import { civilSearchTierLabels } from '~/common/dataRequestUtils';
import { CivilSearchTier, ContactMethod } from '~/common/types';
import { US_STATES } from '~/pages/admin/civilsearch/common/USStates';

/**
 * Field names on the `agencies` table the admin may set when merging
 * agencies. Kept in sync with `MERGEABLE_AGENCY_FIELDS` in `@civilgrid/db`
 * (packages/db/src/schema/agencies.ts), which is the source of truth. When that list changes,
 * mirror the changes here AND update the labels and sections in
 * `pages/admin/civilsearch/agencies/bulkActions/mergeFields.ts`.
 */
export const MERGEABLE_AGENCY_FIELDS = [
  'agencyName',
  'type',
  'isPublished',
  'asbuiltsAvailable',
  'providerId',
  'pdfLink',
  'siteLink',
  'state',
  'outreachEmail',
  'outreachEmailName',
  'publicNotes',
  'doesNotRespond',
  'chargesMapFees',
  'requiresLetterhead',
  'requiresNda',
  'hideResponseEstimate',
  'averageMapFeePerPage',
  'mapFeeNote',
  'preferredContactMethod',
  'prrUrl',
  'portalContactFormUrl',
  'aiContactResearch',
  'logo',
  'restrictions',
  'civilSearchTier',
  'defaultCivilSearchAssigneeId',
  'firstFollowUpDuration',
  'secondFollowUpDuration',
  'thirdFollowUpDuration',
  'fourthFollowUpDuration',
  'timeoutDuration',
] as const;

export type MergeableAgencyField = (typeof MERGEABLE_AGENCY_FIELDS)[number];

/**
 * Mirror of `AgencyType` in packages/db/src/schema/agencies.ts, which the client cannot import.
 * `test/common/mergeableAgencyFields.test.ts` fails when the two lists drift.
 */
export const AGENCY_TYPES = [
  'Multiple',
  'Water',
  'Recycled',
  'Electric',
  'Sewer',
  'Storm',
  'Gas',
  'Telco',
  'Fiber',
  'Cable',
  'Other',
  'Pipeline',
  'Fiber-to-the-Premises',
  'Unknown',
  'Railroad',
  'Streetlights / Traffic Signals',
  'Steam',
  'Geothermal',
  'Paving Projects',
  'Moratoriums',
  'CIP',
] as const;

export const CONTACT_METHOD_LABELS: Record<ContactMethod, string> = {
  [ContactMethod.Email]: 'Email',
  [ContactMethod.PublicRecordsRequest]: 'PRR',
  [ContactMethod.PortalContactForm]: 'Portal Contact Form',
};

export type MergeFieldOption = { value: string; label: string };

/**
 * How the merge dialog's Custom control edits a field. `select` and `boolean` accept only their
 * options, `number` is validated as a number (`integer` as a whole number), `url` and `email` are
 * format-checked, and `pick-only` fields have no Custom control at all.
 */
export type MergeFieldInput =
  | { kind: 'text'; multiline?: boolean }
  | { kind: 'url' }
  | { kind: 'email' }
  | { kind: 'number'; integer: boolean }
  | { kind: 'select'; options: readonly MergeFieldOption[] }
  | { kind: 'boolean' }
  | { kind: 'pick-only' };

const text: MergeFieldInput = { kind: 'text' };
const multiline: MergeFieldInput = { kind: 'text', multiline: true };
const url: MergeFieldInput = { kind: 'url' };
const boolean: MergeFieldInput = { kind: 'boolean' };
const duration: MergeFieldInput = { kind: 'number', integer: true };
const pickOnly: MergeFieldInput = { kind: 'pick-only' };

export const MERGE_FIELD_INPUT: Record<MergeableAgencyField, MergeFieldInput> = {
  agencyName: text,
  type: { kind: 'select', options: AGENCY_TYPES.map((value) => ({ value, label: value })) },
  isPublished: boolean,
  asbuiltsAvailable: boolean,
  providerId: text,
  pdfLink: url,
  siteLink: url,
  state: { kind: 'select', options: US_STATES },
  outreachEmail: { kind: 'email' },
  outreachEmailName: text,
  publicNotes: multiline,
  doesNotRespond: boolean,
  chargesMapFees: boolean,
  requiresLetterhead: boolean,
  requiresNda: boolean,
  hideResponseEstimate: boolean,
  averageMapFeePerPage: { kind: 'number', integer: false },
  mapFeeNote: multiline,
  preferredContactMethod: {
    kind: 'select',
    options: Object.values(ContactMethod).map((value) => ({ value, label: CONTACT_METHOD_LABELS[value] })),
  },
  prrUrl: url,
  portalContactFormUrl: url,
  aiContactResearch: multiline,
  logo: pickOnly,
  restrictions: pickOnly,
  civilSearchTier: {
    kind: 'select',
    options: Object.values(CivilSearchTier).map((value) => ({ value, label: civilSearchTierLabels[value] })),
  },
  defaultCivilSearchAssigneeId: pickOnly,
  firstFollowUpDuration: duration,
  secondFollowUpDuration: duration,
  thirdFollowUpDuration: duration,
  fourthFollowUpDuration: duration,
  timeoutDuration: duration,
};
